import { useRef, useState } from 'react';
import type { TrainingGroupResponse } from '@bm/contracts';
import { useClickOutside } from '../lib/use-click-outside';
import { WD } from '../lib/weekday';

/** Multi-select group filter ("JTG1"/"JTG2"/"O19"/…) — shared by the
 * Training-Übersicht and der Trainer-Arbeitsseite (§ Nutzeranfrage). Purely a
 * display narrowing on top of what the backend already allows the caller to
 * see — picking a group you're not a member of correctly yields an empty
 * list rather than leaking that group's sessions. */
export function GroupFilterDropdown({
  groups,
  selected,
  onChange,
}: {
  groups: TrainingGroupResponse[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }
  const label =
    selected.length === 0
      ? 'Alle Gruppen'
      : selected.length === 1
        ? (groups.find((g) => g.id === selected[0])?.name ?? '1 Gruppe')
        : `${selected.length} Gruppen`;

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside(rootRef, () => setOpen(false), open);

  return (
    <div className="relative min-w-0 shrink" ref={rootRef}>
      {/* `min-w-0` + `truncate` (statt nur `whitespace-nowrap`) lässt den
          Button in einer `flex-nowrap`-Zeile mit zu wenig Platz schrumpfen
          (Text wird mit Ellipse gekürzt) statt zu überlaufen oder die Zeile
          umbrechen zu lassen — ohne das hier gesetzte Breiten-Minimum, das
          jeder Flex-Child standardmäßig von seinem Inhalt erbt, würde
          `flex-nowrap` allein nichts nützen (§ Nutzerfeedback Training
          „sollen alle in eine Zeile … Größe … automatisch skalieren"). Ohne
          umgebende Enge (der häufigere Fall, `flex-wrap`-Zeilen auf anderen
          Seiten) ändert sich dadurch nichts sichtbar. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full min-w-0 cursor-pointer truncate rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
      >
        {label} ▾
      </button>
      {open && (
        <div className="absolute left-0 z-10 mt-1 w-56 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          {groups.length === 0 && <p className="px-2 py-1 text-sm text-slate-400">Keine Gruppen</p>}
          {groups.map((g) => (
            <label key={g.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
              <input type="checkbox" checked={selected.includes(g.id)} onChange={() => toggle(g.id)} />
              {g.name}
            </label>
          ))}
          {selected.length > 0 && (
            <button
              type="button"
              className="mt-1 w-full rounded px-2 py-1 text-left text-xs text-blue-600 hover:bg-slate-50"
              onClick={() => onChange([])}
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Nach Wochentag filtern (§ Nutzeranfrage) — gleiches Dropdown-Muster wie
 * `GroupFilterDropdown` oben, nur mit den sieben festen Wochentagen statt
 * einer dynamischen Liste. ISO-Wochentage (1=Montag…7=Sonntag), wie
 * `TrainingSeries.weekdays`. Shared by Training-Übersicht, Trainer-
 * Arbeitsseite und der Trainer-Monatsübersicht. `availableDays` (optional,
 * § Nutzeranfrage: "zeige im Wochentagfilter nur die Tage an, wo
 * Trainingstage stattfinden") beschränkt die angezeigten Checkboxen auf
 * ISO-Wochentage, an denen es tatsächlich Daten gibt — ohne diese Prop
 * bleibt es bei allen sieben (bisheriges Verhalten). */
export function WeekdayFilterDropdown({
  selected,
  onChange,
  availableDays,
}: {
  selected: number[];
  onChange: (days: number[]) => void;
  availableDays?: number[];
}) {
  function toggle(day: number) {
    onChange(selected.includes(day) ? selected.filter((d) => d !== day) : [...selected, day]);
  }
  const label =
    selected.length === 0
      ? 'Alle Wochentage'
      : selected.length === 1
        ? WD[selected[0]! - 1]
        : `${selected.length} Wochentage`;
  const days = availableDays ? WD.map((label, i) => ({ label, day: i + 1 })).filter((d) => availableDays.includes(d.day)) : WD.map((label, i) => ({ label, day: i + 1 }));

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside(rootRef, () => setOpen(false), open);

  return (
    <div className="relative min-w-0 shrink" ref={rootRef}>
      {/* See GroupFilterDropdown above for why `min-w-0`/`truncate` instead
          of plain `whitespace-nowrap`. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full min-w-0 cursor-pointer truncate rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
      >
        {label} ▾
      </button>
      {/* `right-0` statt `left-0` (anders als GroupFilterDropdown oben, das
          typischerweise als erstes/linkes Element in der Filterzeile steht)
          — dieser Button sitzt meist weiter rechts in der Zeile, ein von
          seiner linken Kante aus geöffnetes Menü lief dort auf schmalen
          Bildschirmen über den Seitenrand hinaus (§ Nutzerfeedback "Dropdown
          Menü ist außerhalb des Bildes"). Wächst jetzt von der rechten
          Button-Kante nach links statt nach rechts. */}
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-44 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          {days.length === 0 && <p className="px-2 py-1 text-sm text-slate-400">Keine Trainingstage</p>}
          {days.map(({ label, day }) => (
            <label key={label} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
              <input type="checkbox" checked={selected.includes(day)} onChange={() => toggle(day)} />
              {label}
            </label>
          ))}
          {selected.length > 0 && (
            <button
              type="button"
              className="mt-1 w-full rounded px-2 py-1 text-left text-xs text-blue-600 hover:bg-slate-50"
              onClick={() => onChange([])}
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
